import { pluralize, Tooltip } from '@leland-dev/leland-ui-library';
import React, { type FC, useState } from 'react';

import { useCountRows } from '../../utils/hooks/useCountRows';
import { OrganizationImg, OrganizationType } from '../common/OrganizationImg';

import { type CareerOutcomesCareerOutcomeSummaryFragment } from './__generated-gql-types__/CareerOutcome.generated';

interface CareerOutcomesProps {
  outcomes: CareerOutcomesCareerOutcomeSummaryFragment;
  coachName: Possible<string>;
  className?: string;
  truncateAfterLines?: number;
  isButtonBelowLogos?: boolean;
}

export const CareerOutcomes: FC<CareerOutcomesProps> = ({
  outcomes,
  coachName,
  className = '',
  truncateAfterLines = 1,
  isButtonBelowLogos,
}) => {
  const [seeAllOrganizations, setSeeAllOrganizations] = useState(false);
  const [organizationImgLines, organizationImgRef] = useCountRows();

  const isTruncated = truncateAfterLines < organizationImgLines ? true : null;

  const maxHeight =
    truncateAfterLines === 1
      ? 'max-h-9'
      : truncateAfterLines === 3
        ? 'max-h-32'
        : '';

  return (
    <div className={`w-full ${className}`}>
      {outcomes.companies.length > 1 ? (
        <div className="mb-3 flex w-full flex-col justify-between md:flex-row">
          <p className="text-lg font-normal text-leland-gray-light">
            {coachName
              ? `${coachName} has helped clients get into these companies:`
              : 'Customers who purchased packages have gotten into these companies:'}
          </p>
          {isTruncated && !isButtonBelowLogos ? (
            <button
              className="link link--primary text-left text-base font-medium"
              onClick={() => setSeeAllOrganizations((oldVar) => !oldVar)}
              id="career_outcomes--see_all_companies"
            >
              {seeAllOrganizations
                ? 'Show fewer'
                : `See all ${pluralize(outcomes.companies.length, 'company')}`}
            </button>
          ) : null}
        </div>
      ) : null}
      <div>
        <div
          className={`flex flex-wrap ${isTruncated && !seeAllOrganizations ? `${maxHeight} overflow-hidden` : ''} gap-1.5`}
          ref={organizationImgRef}
        >
          {outcomes.companies.map((company) => (
            <Tooltip key={company.id} content={company.name}>
              <OrganizationImg
                key={company.id}
                name={company.name}
                pictureLink={company.logoLink}
                type={OrganizationType.COMPANY}
                nonSizeClassName="rounded-sm"
                large
              />
            </Tooltip>
          ))}

          {outcomes.companies.length === 1 ? (
            <p className="ml-2 w-48 flex-wrap text-leland-gray-light">
              {coachName} has helped Leland clients get into{' '}
              {outcomes.companies[0].name}
            </p>
          ) : null}
        </div>
        {isTruncated && isButtonBelowLogos ? (
          <button
            className="link link--primary mt-4 text-left text-base font-medium"
            onClick={() => setSeeAllOrganizations((oldVar) => !oldVar)}
            id="career_outcomes--see_all_companies"
          >
            {seeAllOrganizations
              ? 'Show fewer'
              : `See all ${pluralize(outcomes.companies.length, 'company')}`}
          </button>
        ) : null}
      </div>
    </div>
  );
};
